// Taxonomía de fútbol (Scout QA #24). Fuente ÚNICA de posiciones, grupos,
// lateralidad y tags/atributos para el matching scout ↔ atleta.
//
// Football-only por ahora. Cada valor tiene un `code` estable (no cambiar: se
// guarda en attributes.* y lo usa la RPC) y un `label` en español para la UI.
// La capa de IA (enriquecimiento) y la semántica cubren el long-tail; estos
// códigos dan la captura guiada + el score explicable.

// ── Deporte al que aplica esta taxonomía ──────────────────────────────────
// Athletain es solo fútbol: no se ofrece elegir deporte y los scouts no ven
// atletas de otros deportes (quedan perfiles legados de antes de acotarlo).
export const FOOTBALL_SPORT_LABEL = "Fútbol";

// ── Grupos de posición (para match parcial: "un defensa" vs "lateral izq") ──
export const POSITION_GROUPS = [
  { code: "porteria", label: "Portería" },
  { code: "defensa", label: "Defensa" },
  { code: "mediocampo", label: "Mediocampo" },
  { code: "ataque", label: "Ataque" },
];

// ── Posiciones ────────────────────────────────────────────────────────────
export const FOOTBALL_POSITIONS = [
  { code: "portero", label: "Portero", group: "porteria" },
  { code: "defensa_central", label: "Defensa central", group: "defensa" },
  { code: "lateral_derecho", label: "Lateral derecho", group: "defensa" },
  { code: "lateral_izquierdo", label: "Lateral izquierdo", group: "defensa" },
  { code: "carrilero_derecho", label: "Carrilero derecho", group: "defensa" },
  { code: "carrilero_izquierdo", label: "Carrilero izquierdo", group: "defensa" },
  {
    code: "mediocentro_defensivo",
    label: "Mediocentro defensivo",
    group: "mediocampo",
  },
  { code: "mediocentro", label: "Mediocentro", group: "mediocampo" },
  {
    code: "mediocentro_ofensivo",
    label: "Mediocentro ofensivo",
    group: "mediocampo",
  },
  { code: "extremo_derecho", label: "Extremo derecho", group: "ataque" },
  { code: "extremo_izquierdo", label: "Extremo izquierdo", group: "ataque" },
  { code: "delantero_centro", label: "Delantero centro", group: "ataque" },
  { code: "segundo_delantero", label: "Segundo delantero", group: "ataque" },
];

// ── Lateralidad ───────────────────────────────────────────────────────────
export const LATERALITY_OPTIONS = [
  { code: "diestro", label: "Diestro" },
  { code: "zurdo", label: "Zurdo" },
  { code: "ambidiestro", label: "Ambidiestro" },
];

// ── Categorías de tags ────────────────────────────────────────────────────
export const TAG_CATEGORIES = [
  { code: "fisico", label: "Físico" },
  { code: "tecnico", label: "Técnico" },
  { code: "tactico", label: "Táctico" },
  { code: "mental", label: "Mental" },
];

// ── Tags / atributos ──────────────────────────────────────────────────────
export const FOOTBALL_TAGS = [
  // Físico
  { code: "velocidad", label: "Velocidad", category: "fisico" },
  { code: "aceleracion", label: "Aceleración", category: "fisico" },
  { code: "resistencia", label: "Resistencia", category: "fisico" },
  { code: "fuerza", label: "Fuerza", category: "fisico" },
  { code: "agilidad", label: "Agilidad", category: "fisico" },
  { code: "salto_cabeceo", label: "Salto / juego aéreo", category: "fisico" },
  // Técnico
  { code: "control", label: "Control de balón", category: "tecnico" },
  { code: "regate", label: "Regate", category: "tecnico" },
  { code: "pase_corto", label: "Pase corto", category: "tecnico" },
  { code: "pase_largo", label: "Pase largo", category: "tecnico" },
  { code: "definicion", label: "Definición", category: "tecnico" },
  { code: "remate_lejano", label: "Remate de media distancia", category: "tecnico" },
  { code: "dos_pies", label: "Dominio de ambos pies", category: "tecnico" },
  { code: "centros", label: "Centros", category: "tecnico" },
  { code: "tiros_libres", label: "Tiros libres", category: "tecnico" },
  { code: "penales", label: "Penales", category: "tecnico" },
  { code: "primer_toque", label: "Primer toque", category: "tecnico" },
  // Táctico
  { code: "vision", label: "Visión de juego", category: "tactico" },
  { code: "posicionamiento", label: "Posicionamiento", category: "tactico" },
  { code: "anticipacion", label: "Anticipación", category: "tactico" },
  { code: "marcaje", label: "Marcaje", category: "tactico" },
  { code: "presion", label: "Presión", category: "tactico" },
  { code: "desmarque", label: "Desmarque", category: "tactico" },
  { code: "cobertura", label: "Cobertura", category: "tactico" },
  { code: "transicion", label: "Transiciones", category: "tactico" },
  // Mental
  { code: "liderazgo", label: "Liderazgo", category: "mental" },
  { code: "concentracion", label: "Concentración", category: "mental" },
  { code: "trabajo_equipo", label: "Trabajo en equipo", category: "mental" },
  { code: "competitividad", label: "Competitividad", category: "mental" },
];

// ── Índices para lookup O(1) ──────────────────────────────────────────────
const POSITION_BY_CODE = new Map(FOOTBALL_POSITIONS.map((p) => [p.code, p]));
const TAG_BY_CODE = new Map(FOOTBALL_TAGS.map((t) => [t.code, t]));
const GROUP_BY_CODE = new Map(POSITION_GROUPS.map((g) => [g.code, g]));

// Normaliza texto (quita acentos + minúsculas) — espeja el norm_text de la RPC.
const normText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();

// Alias de etiquetas legadas / texto libre → code. Para migrar datos viejos
// (InitialEvaluation antiguo, position en texto libre de AthleteForm).
const POSITION_ALIASES = {
  // etiquetas viejas de InitialEvaluation
  "mediocampista defensivo": "mediocentro_defensivo",
  "mediocampista central": "mediocentro",
  "mediocampista ofensivo": "mediocentro_ofensivo",
  mediapunta: "mediocentro_ofensivo",
  // texto libre común en prod
  delantero: "delantero_centro",
  defensa: "defensa_central",
  mediocampista: "mediocentro",
  volante: "mediocentro_defensivo",
  "volante 5": "mediocentro_defensivo",
};

// ── Helpers ───────────────────────────────────────────────────────────────
export const getPositionLabel = (code) =>
  POSITION_BY_CODE.get(code)?.label || "";

export const getPositionGroup = (code) =>
  POSITION_BY_CODE.get(code)?.group || null;

export const getGroupLabel = (code) => GROUP_BY_CODE.get(code)?.label || "";

export const getTagLabel = (code) => TAG_BY_CODE.get(code)?.label || code;

export const isValidPositionCode = (code) => POSITION_BY_CODE.has(code);

export const isValidTagCode = (code) => TAG_BY_CODE.has(code);

export const getTagsByCategory = () =>
  TAG_CATEGORIES.map((cat) => ({
    ...cat,
    tags: FOOTBALL_TAGS.filter((t) => t.category === cat.code),
  }));

// Convierte un texto libre / etiqueta legada de posición a un code válido.
// Devuelve null si no reconoce nada (ej. "Base" de baloncesto).
export const positionCodeFromText = (text) => {
  const norm = normText(text);
  if (!norm) return null;
  // 1) match exacto por label
  const byLabel = FOOTBALL_POSITIONS.find((p) => normText(p.label) === norm);
  if (byLabel) return byLabel.code;
  // 2) match por code directo (por si ya viene un code)
  if (POSITION_BY_CODE.has(norm)) return norm;
  // 3) alias legado
  if (POSITION_ALIASES[norm]) return POSITION_ALIASES[norm];
  return null;
};

// Reverse index de tags por label normalizado (para mapear traits del scout).
const TAG_BY_NORM_LABEL = new Map(
  FOOTBALL_TAGS.map((t) => [normText(t.label), t.code]),
);

// Convierte un texto libre / label de trait a un tag code válido, o null.
export const tagCodeFromText = (text) => {
  const norm = normText(text);
  if (!norm) return null;
  if (TAG_BY_CODE.has(norm)) return norm; // ya es un code
  if (TAG_BY_NORM_LABEL.has(norm)) return TAG_BY_NORM_LABEL.get(norm);
  return null;
};

// Grupos únicos a partir de una lista de position codes.
export const positionGroupsFromCodes = (codes = []) => [
  ...new Set(codes.map((c) => getPositionGroup(c)).filter(Boolean)),
];

// Dado un code, arma el objeto estructurado para guardar en attributes.
export const buildPositionFields = (code) => {
  if (!code || !POSITION_BY_CODE.has(code)) {
    return { position_code: null, position_group: null, position_label: "" };
  }
  const pos = POSITION_BY_CODE.get(code);
  return {
    position_code: pos.code,
    position_group: pos.group,
    position_label: pos.label,
  };
};

const FOOTBALL_SPORT_NAMES = new Set(["futbol", "soccer", "football"]);

// Un atleta sin deporte cuenta como de fútbol: casi todos los perfiles legados
// lo tienen vacío y solo se registran futbolistas.
export const isFootballSport = (sport) => {
  const norm = normText(sport);
  return !norm || FOOTBALL_SPORT_NAMES.has(norm);
};

// El deporte puede venir en la columna o solo en attributes (perfiles legados).
export const isFootballAthlete = (athlete) =>
  isFootballSport(athlete?.sport || athlete?.attributes?.sport);

// Code de posición de una fila de atleta: el estructurado si existe, si no el
// texto libre legado (`position`) mapeado a la taxonomía.
export const athletePositionCode = (athlete) => {
  const code = athlete?.attributes?.position_code || athlete?.position_code;
  if (code && POSITION_BY_CODE.has(code)) return code;
  return positionCodeFromText(athlete?.position);
};

// Label de la taxonomía para una posición guardada como texto libre legado
// ("Delantero" → "Delantero centro"), para que el select la muestre elegida.
// Si no se reconoce, devuelve el texto tal cual.
export const footballPositionLabel = (text) =>
  getPositionLabel(positionCodeFromText(text)) || text || "";
